import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { CountrySelect } from "@/components/ui/country-select";
import { UserApi } from "@/api/user.api";
import { MerchantApi } from "@/api/merchant.api";
import { ClientApi } from "@/api/client.api";
import { ProgramsApi } from "@/api/perkpoint/programs";
import { ROLE_CODES } from "@/constants/roles.constant";
import { Badge } from "@/components/ui/badge";
import { Gamepad2, Eye, EyeOff } from "lucide-react";

/** Returns unmet password rules as short messages for live feedback. */
function getMissingPasswordRequirements(password: string): string[] {
  const missing: string[] = [];
  if (password.length < 7) missing.push("At least 7 characters required");
  if (!/[A-Z]/.test(password)) missing.push("One uppercase letter required");
  if (!/[a-z]/.test(password)) missing.push("One lowercase letter required");
  if (!/\d/.test(password)) missing.push("One number required");
  if (!/[^A-Za-z0-9]/.test(password))
    missing.push("One special character required");
  return missing;
}

function isPasswordValid(password: string): boolean {
  return getMissingPasswordRequirements(password).length === 0;
}

const formSchema = z
  .object({
    firstName: z.string().min(2, {
      message: "First name must be at least 2 characters.",
    }),
    lastName: z.string().min(2, {
      message: "Last name must be at least 2 characters.",
    }),
    email: z.string().email({
      message: "Please enter a valid email address.",
    }),
    role: z.string().min(1, {
      message: "Please select a role.",
    }),
    password: z.string().optional(),
    requireEmailVerification: z.boolean().default(false),
    merchantId: z.string().optional(),
    merchantStoreId: z.string().optional(),
    programId: z.string().optional(),
    clientId: z.string().optional(),
    transferPartnerId: z.string().optional(),
    mallId: z.string().optional(),
    countryCode: z.string().default("+971"),
    mobile: z.string().optional(),
  })
  .refine(
    (data) => {
      // If email verification is NOT checked, password is required
      if (!data.requireEmailVerification) {
        return isPasswordValid(data.password || "");
      }
      return true;
    },
    {
      message:
        "Password must be at least 7 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
      path: ["password"],
    }
  );

type CreateUserFormValues = z.infer<typeof formSchema>;

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (user: any) => void;
  onAddUser?: (user: any) => void;
  roles?: any[];
  merchants?: any[];
  programs?: any[];
  clients?: any[];
}

export function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
  onAddUser,
  roles = [],
  merchants = [],
  programs = [],
  clients = [],
}: CreateUserDialogProps) {
  const [merchantStores, setMerchantStores] = useState<any[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [clientPrograms, setClientPrograms] = useState<any[]>([]);
  const [transferPartners, setTransferPartners] = useState<any[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [perkPointPrograms, setPerkPointPrograms] = useState<
    { id: string; name: string; programCode: string }[]
  >([]);
  const [loadingPerkPointPrograms, setLoadingPerkPointPrograms] =
    useState(false);
  const [step, setStep] = useState(1);
  const [selectedApplications, setSelectedApplications] = useState<string[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<CreateUserFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      password: "",
      requireEmailVerification: false,
      merchantId: "",
      merchantStoreId: "",
      programId: "",
      clientId: "",
      transferPartnerId: "",
      mallId: "",
      countryCode: "+971",
      mobile: "",
    },
  });

  const roleWatch = form.watch("role");
  const merchantIdWatch = form.watch("merchantId");
  const clientIdWatch = form.watch("clientId");
  const requireEmailVerificationWatch = form.watch("requireEmailVerification");
  const passwordWatch = form.watch("password") || "";
  const isCreatePasswordValid = isPasswordValid(passwordWatch);

  useEffect(() => {
    if (open) {
      form.reset({
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        password: "",
        requireEmailVerification: false,
        merchantId: "",
        merchantStoreId: "",
        programId: "",
        clientId: "",
        transferPartnerId: "",
        mallId: "",
        countryCode: "+971",
        mobile: "",
      });
      setStep(1);
    }
  }, [open]);

  useEffect(() => {
    const fetchStores = async () => {
      console.log("Role Watch:", roleWatch);
      console.log("Merchant ID Watch:", merchantIdWatch);

      const selectedRole = roles.find((role) => role.roleCode === roleWatch);
      console.log("Selected Role:", selectedRole);

      if (merchantIdWatch && selectedRole?.roleCode === "MERCHANT_APP") {
        console.log("Fetching stores for merchant:", merchantIdWatch);
        setLoadingStores(true);
        try {
          const response = await MerchantApi.getStoresUsingMerchantId(
            merchantIdWatch
          );
          console.log("Stores response:", response);
          setMerchantStores(response.data || []);
        } catch (error) {
          console.error("Error fetching stores:", error);
          setMerchantStores([]);
        } finally {
          setLoadingStores(false);
        }
      } else {
        setMerchantStores([]);
      }
    };

    fetchStores();
  }, [merchantIdWatch, roleWatch, roles]);

  useEffect(() => {
    if (roleWatch === "PROGRAM_ADMIN") {
      const fetchClientPrograms = async () => {
        if (clientIdWatch) {
          setLoadingPrograms(true);
          try {
            const response = await ClientApi.getClientProgramsAndPartners(
              Number(clientIdWatch)
            );
            const allPrograms = [
              ...(response.data.earnProgramVOs || []),
              ...(response.data.burnProgramVOs || []),
              ...(response.data.transferProgramVOs || []),
            ];
            setClientPrograms(allPrograms);
            setTransferPartners(response.data.transferPartnerVOs || []);
          } catch (error) {
            setClientPrograms([]);
            setTransferPartners([]);
          } finally {
            setLoadingPrograms(false);
          }
        } else {
          setClientPrograms([]);
          setTransferPartners([]);
        }
      };
      fetchClientPrograms();
    } else if (roleWatch === "TRANSFER_PARTNER_APP") {
      const fetchTransferPartners = async () => {
        if (clientIdWatch) {
          setLoadingPrograms(true);
          try {
            const response = await ClientApi.getClientProgramsAndPartners(
              Number(clientIdWatch)
            );
            setTransferPartners(response.data.transferPartnerVOs || []);
          } catch (error) {
            setTransferPartners([]);
          } finally {
            setLoadingPrograms(false);
          }
        } else {
          setTransferPartners([]);
        }
      };
      fetchTransferPartners();
    }
  }, [clientIdWatch, roleWatch]);

  useEffect(() => {
    if (roleWatch !== ROLE_CODES.PERK_POINT_PROGRAM) {
      setPerkPointPrograms([]);
      return;
    }

    let cancelled = false;
    const fetchPerkPointPrograms = async () => {
      setLoadingPerkPointPrograms(true);
      try {
        const response = await ProgramsApi.list({ pageSize: 100 });
        if (!cancelled) {
          setPerkPointPrograms(
            (response.items || []).map((program) => ({
              id: program.id,
              name: program.name,
              programCode: program.programCode,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching Perk Point programs:", error);
        if (!cancelled) {
          setPerkPointPrograms([]);
          toast.error("Failed to load Perk Point programs");
        }
      } finally {
        if (!cancelled) setLoadingPerkPointPrograms(false);
      }
    };

    fetchPerkPointPrograms();
    return () => {
      cancelled = true;
    };
  }, [roleWatch]);

  useEffect(() => {
    if (roleWatch === "GIFT_CARD_ADMIN") {
      setSelectedApplications((prev) => {
        if (!prev.includes("gift_card")) {
          return ["gift_card"];
        }
        return prev;
      });
    } else if (roleWatch === "OFFER_POINT_ADMIN") {
      setSelectedApplications((prev) =>
        prev.includes("offer_point") ? prev : [...prev, "offer_point"]
      );
    } else if (roleWatch === "GAMIFICATION_ADMIN") {
      setSelectedApplications((prev) =>
        prev.includes("gamification") ? prev : [...prev, "gamification"]
      );
    } else if (
      roleWatch === ROLE_CODES.PERK_POINT_PROGRAM ||
      roleWatch === ROLE_CODES.PERK_POINT_ADMIN
    ) {
      // Perk Point roles may only access Perk Point
      setSelectedApplications(["perk_point"]);
    } else {
      setSelectedApplications((prev) =>
        prev.filter((app) => app !== "gift_card")
      );
    }
  }, [roleWatch]);

  async function onSubmit(values: CreateUserFormValues) {
    if (isSubmitting) return;

    if (!values.requireEmailVerification) {
      const missing = getMissingPasswordRequirements(values.password || "");
      if (missing.length > 0) {
        toast.error(missing.join(". "));
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const selectedRole = roles.find((role) => role.roleCode === values.role);

      const userVO: any = {
        username: values.email,
        name: `${values.firstName} ${values.lastName}`,
        roleVO:
          values.role === "GAMIFICATION_ADMIN"
            ? { id: 11, roleName: "Gamification admin" }
            : {
                id: selectedRole?.id || 0,
                roleName: selectedRole?.roleName,
              },
        phoneCountryCode: values.countryCode,
        phoneNumber: values.mobile || "",
        clientId: values.clientId ? Number(values.clientId) : undefined,
        transferPartnerId: values.transferPartnerId
          ? Number(values.transferPartnerId)
          : undefined,
        password: values.requireEmailVerification ? null : values.password || null,
        // Checked "Require email verification" → skipEmailVerification false
        // Password entered (no email verification) → skipEmailVerification true
        skipEmailVerification: !values.requireEmailVerification,
      };

      // Add conditional fields based on role
      if (
        selectedRole?.roleName === "Merchant Admin" ||
        selectedRole?.roleName === "Merchant Application"
      ) {
        userVO.merchantId = parseInt(values.merchantId || "0");
      }

      if (selectedRole?.roleName === "Merchant Application") {
        userVO.merchantStoreId = parseInt(values.merchantStoreId || "0");
      }

      if (selectedRole?.roleName === "Program Admin") {
        userVO.programId = parseInt(values.programId || "0");
        userVO.clientId = parseInt(values.clientId || "0");
      }

      if (selectedRole?.roleCode === ROLE_CODES.PERK_POINT_PROGRAM) {
        const selectedProgram = perkPointPrograms.find(
          (program) => program.id === values.programId
        );
        if (selectedProgram) {
          const numericId = Number(selectedProgram.id);
          userVO.perkProgramDetails = {
            programId: Number.isFinite(numericId) ? numericId : selectedProgram.id,
            programCode: selectedProgram.programCode,
          };
        }
      }

      if (selectedRole?.roleName === "Gift Card Admin") {
        userVO.mallId = values.mallId ? parseInt(values.mallId) : undefined;
      }

      const applicationMap = {
        access_point: { id: 1, name: "ACCESSPOINT" },
        reward_point: { id: 3, name: "REWARDPOINT" },
        xpand_point: { id: 2, name: "XPANDPOINT" },
        gift_card: { id: 4, name: "GIFTCARD" },
        perk_point: { id: 5, name: "PERK_POINT" },
        offer_point: { id: 6, name: "OFFERPOINT" },
        gamification: { id: 7, name: "GAMIFICATION" },
      };
      // const applicationMap = {
      //   access_point: { id: 1, name: "ACCESSPOINT" },
      //   reward_point: { id: 2, name: "REWARDPOINT" },
      //   xpand_point: { id: 3, name: "XPANDPOINT" },
      // };
      userVO.applicationVOs = selectedApplications.map(
        (key) => applicationMap[key]
      );

      console.log("User VO:", userVO);

      const response = await UserApi.createOrUpdateUser({
        userVO,
      });

      if (response.data) {
        toast.success(`User ${values.email} created successfully`);

        if (values.requireEmailVerification) {
          toast("Verification email sent to user");
        }

        const newUser = {
          id:
            response.data.id?.toString() ||
            Math.random().toString(36).substring(2, 8),
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
          status: values.requireEmailVerification ? "Pending" : "Active",
          createdAt: new Date().toISOString(),
        };

        if (onCreated) {
          onCreated(newUser);
        }

        if (onAddUser) {
          onAddUser(newUser);
        }

        form.reset({
          firstName: "",
          lastName: "",
          email: "",
          role: "",
          password: "",
          requireEmailVerification: false,
          merchantId: "",
          merchantStoreId: "",
          programId: "",
          clientId: "",
          transferPartnerId: "",
          mallId: "",
          countryCode: "+971",
          mobile: "",
        });
        setStep(1);
        onOpenChange(false);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.message ||
          "Failed to create user"
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const renderRoleDependentFields = () => {
    const selectedRole = roles.find((role) => role.roleCode === roleWatch);
    console.log("Rendering fields for role:", selectedRole);

    if (!selectedRole) return null;

    switch (selectedRole.roleCode) {
      case "MERCHANT_ADMIN":
        return (
          <FormField
            control={form.control}
            name="merchantId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Merchant</FormLabel>
                <Select
                  onValueChange={(value) => {
                    console.log("Selected merchant:", value);
                    field.onChange(value);
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a merchant" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent
                    className="!max-h-[280px]"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {merchants.map((merchant) => (
                      <SelectItem
                        key={merchant.id}
                        value={merchant.id.toString()}
                      >
                        {merchant.merchantName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "MERCHANT_APP":
        return (
          <>
            <FormField
              control={form.control}
              name="merchantId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Merchant</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      console.log("Selected merchant:", value);
                      field.onChange(value);
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a merchant" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent
                      className="!max-h-[280px]"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {merchants.map((merchant) => (
                        <SelectItem
                          key={merchant.id}
                          value={merchant.id.toString()}
                        >
                          {merchant.merchantName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {merchantIdWatch && (
              <FormField
                control={form.control}
                name="merchantStoreId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={loadingStores}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              loadingStores
                                ? "Loading stores..."
                                : "Select a store"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent
                        className="!max-h-[280px]"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {merchantStores.map((store) => (
                          <SelectItem
                            key={store.id}
                            value={store.id.toString()}
                          >
                            {store.address}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </>
        );

      case "PROGRAM_ADMIN":
        return (
          <>
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.setValue("programId", "");
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent
                      className="!max-h-[280px]"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {clients.map((client) => (
                        <SelectItem
                          key={client.id}
                          value={client.id.toString()}
                        >
                          {client.clientName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        );

      case "TRANSFER_PARTNER_APP":
        return (
          <>
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.setValue("transferPartnerId", "");
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent
                      className="!max-h-[280px]"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {clients.map((client) => (
                        <SelectItem
                          key={client.id}
                          value={client.id.toString()}
                        >
                          {client.clientName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {clientIdWatch && (
              <FormField
                control={form.control}
                name="transferPartnerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transfer Partner</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={loadingPrograms}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              loadingPrograms
                                ? "Loading partners..."
                                : "Select a transfer partner"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent
                        className="!max-h-[280px]"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {transferPartners.map((partner) => (
                          <SelectItem
                            key={partner.id}
                            value={partner.id.toString()}
                          >
                            {partner.partnerName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </>
        );

      case "ONLINE_MERCHANT":
        return (
          <FormField
            control={form.control}
            name="merchantId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Merchant</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a merchant" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent
                    className="!max-h-[280px]"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {merchants.map((merchant) => (
                      <SelectItem
                        key={merchant.id}
                        value={merchant.id.toString()}
                      >
                        {merchant.merchantName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "PROGRAM_MEMBER":
        return (
          <>
            <FormField
              control={form.control}
              name="clientId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent
                      className="!max-h-[280px]"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {clients.map((client) => (
                        <SelectItem
                          key={client.id}
                          value={client.id.toString()}
                        >
                          {client.clientName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="programId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Program</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a program" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent
                      className="!max-h-[280px]"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {programs.map((program) => (
                        <SelectItem
                          key={program.id}
                          value={program.id.toString()}
                        >
                          {program.programName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        );

      case "GIFT_CARD_ADMIN":
        return (
          <FormField
            control={form.control}
            name="mallId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mall ID</FormLabel>
                <FormControl>
                  <Input placeholder="Enter Mall ID" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case ROLE_CODES.PERK_POINT_PROGRAM:
        return (
          <FormField
            control={form.control}
            name="programId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Perk Point Program*</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={loadingPerkPointPrograms}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingPerkPointPrograms
                            ? "Loading programs..."
                            : "Select a Perk Point program"
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent
                    className="!max-h-[280px]"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {perkPointPrograms.length === 0 &&
                    !loadingPerkPointPrograms ? (
                      <div className="px-2 py-3 text-sm text-muted-foreground">
                        No programs found
                      </div>
                    ) : (
                      perkPointPrograms.map((program) => (
                        <SelectItem key={program.id} value={program.id}>
                          {program.name}
                          {program.programCode
                            ? ` (${program.programCode})`
                            : ""}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case "LOYYAL_ADMIN":
        return null;

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl "
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Add a new user to your organization. They'll receive an email
            invitation to join.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center gap-4 mb-6">
          <div
            className={`flex items-center gap-2 ${
              step === 1 ? "font-bold text-blue-600" : "text-gray-400"
            }`}
          >
            1 <span className="hidden md:inline">Basic Info</span>
          </div>
          <div
            className={`h-0.5 w-8 ${step > 1 ? "bg-blue-600" : "bg-gray-200"}`}
          ></div>
          <div
            className={`flex items-center gap-2 ${
              step === 2 ? "font-bold text-blue-600" : "text-gray-400"
            }`}
          >
            2 <span className="hidden md:inline">Role</span>
          </div>
          <div
            className={`h-0.5 w-8 ${step > 2 ? "bg-blue-600" : "bg-gray-200"}`}
          ></div>
          <div
            className={`flex items-center gap-2 ${
              step === 3 ? "font-bold text-blue-600" : "text-gray-400"
            }`}
          >
            3 <span className="hidden md:inline">Application</span>
          </div>
          <div
            className={`h-0.5 w-8 ${step > 3 ? "bg-blue-600" : "bg-gray-200"}`}
          ></div>
          <div
            className={`flex items-center gap-2 ${
              step === 4 ? "font-bold text-blue-600" : "text-gray-400"
            }`}
          >
            4 <span className="hidden md:inline">Security</span>
          </div>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {step === 1 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name*</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="john.doe@example.com"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <FormLabel>Mobile Number (optional)</FormLabel>
                    <div className="flex gap-2">
                      <FormField
                        control={form.control}
                        name="countryCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <CountrySelect
                                value={field.value}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="mobile"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input placeholder="e.g. 1234567890" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {step === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role*</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("programId", "");
                          form.setValue("clientId", "");
                          form.setValue("merchantId", "");
                          form.setValue("merchantStoreId", "");
                          form.setValue("transferPartnerId", "");
                          form.setValue("mallId", "");
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent
                          className="!max-h-[280px]"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          {roles.map((role) => (
                            <SelectItem
                              key={role.id || role.roleCode}
                              value={role.roleCode}
                            >
                              {role.roleName
                                ? role.roleName
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (c) => c.toUpperCase())
                                : (role.roleCode || "")
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {renderRoleDependentFields()}
              </div>
            )}
            {step === 3 && (
              <div className="space-y-4">
                <div className="font-semibold mb-2">Select Application(s)*</div>
                <div
                  className="max-h-[400px] overflow-y-auto overflow-x-hidden pr-1"
                  onWheel={(e) => e.stopPropagation()}
                >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    const selectedRole = roles.find(
                      (role) => role.roleCode === roleWatch
                    );
                    const isGiftCardAdmin =
                      selectedRole?.roleCode === "GIFT_CARD_ADMIN";
                    const isPerkPointRole =
                      selectedRole?.roleCode === ROLE_CODES.PERK_POINT_ADMIN ||
                      selectedRole?.roleCode === ROLE_CODES.PERK_POINT_PROGRAM;

                    const perkPointOnly = [
                      {
                        value: "perk_point",
                        label: "Perk Point",
                        icon: "/app-icons/PerkPoint.svg",
                        description: "Loyalty Management System (LMS).",
                      },
                    ];

                    const applications = isGiftCardAdmin
                      ? [
                          {
                            value: "gift_card",
                            label: "Gift Card",
                            icon: "/app-icons/GiftCard.svg",
                            description:
                              "Manage gift cards and gift card operations.",
                          },
                        ]
                      : isPerkPointRole
                        ? perkPointOnly
                        : [
                            {
                              value: "access_point",
                              label: "Access Point",
                              icon: "/app-icons/AccessPoint.svg",
                              description:
                                "Access platform features and manage entry points.",
                            },
                            {
                              value: "reward_point",
                              label: "Reward Point",
                              icon: "/app-icons/RewardPoint.svg",
                              description:
                                "Manage and redeem reward points for users.",
                            },
                            {
                              value: "xpand_point",
                              label: "Xpand Point",
                              icon: "/app-icons/XpandPoint.svg",
                              description:
                                "Expand your reach with Xpand Point integrations.",
                            },
                            {
                              value: "perk_point",
                              label: "Perk Point",
                              icon: "/app-icons/PerkPoint.svg",
                              description: "Loyalty Management System (LMS).",
                            },
                            {
                              value: "offer_point",
                              label: "Offer Point",
                              icon: "/app-icons/Offers.svg",
                              description:
                                "Programs, merchants, and promotional campaigns.",
                            },
                            {
                              value: "gift_card",
                              label: "Gift Card",
                              icon: "/app-icons/GiftCard.svg",
                              description:
                                "Manage gift cards and gift card operations.",
                            },
                            {
                              value: "gamification",
                              label: "Game Point",
                              icon: null,
                              description:
                                "Spin wheels, scratch cards, quizzes, and more.",
                            },
                          ];

                    return applications;
                  })().map((app) => {
                    const selectedRole = roles.find(
                      (role) => role.roleCode === roleWatch
                    );
                    const isGiftCardAdmin =
                      selectedRole?.roleCode === "GIFT_CARD_ADMIN";
                    const isPerkPointRole =
                      selectedRole?.roleCode === ROLE_CODES.PERK_POINT_ADMIN ||
                      selectedRole?.roleCode === ROLE_CODES.PERK_POINT_PROGRAM;
                    const isGiftCard = app.value === "gift_card";
                    const isPerkPoint = app.value === "perk_point";
                    const isDisabled =
                      (isGiftCardAdmin &&
                        isGiftCard &&
                        selectedApplications.includes("gift_card")) ||
                      (isPerkPointRole &&
                        isPerkPoint &&
                        selectedApplications.includes("perk_point"));

                    return (
                      <button
                        key={app.value}
                        type="button"
                        disabled={isDisabled}
                        className={`border rounded-lg p-4 flex flex-col items-center transition focus:outline-none focus:ring-2 focus:ring-blue-500 select-none shadow-sm hover:shadow-md ${
                          isDisabled
                            ? "cursor-not-allowed opacity-75"
                            : "cursor-pointer"
                        } ${
                          selectedApplications.includes(app.value)
                            ? "border-blue-600 bg-blue-50"
                            : "border-gray-200 bg-white"
                        }`}
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedApplications((prev) =>
                            prev.includes(app.value)
                              ? prev.filter((v) => v !== app.value)
                              : [...prev, app.value]
                          );
                        }}
                      >
                        {app.icon ? (
                          <img
                            src={app.icon}
                            alt={app.label}
                            className="w-10 h-10 mb-2"
                          />
                        ) : (
                          <div className="w-10 h-10 mb-2 flex items-center justify-center rounded-lg bg-orange-100">
                            <Gamepad2 className="w-6 h-6 text-orange-600" />
                          </div>
                        )}
                        <div className="font-semibold mb-1">{app.label}</div>
                        <div className="text-xs text-gray-500 text-center">
                          {app.description}
                        </div>
                        <div
                          className={`mt-3 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedApplications.includes(app.value)
                              ? "border-blue-600 bg-blue-600"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          {selectedApplications.includes(app.value) && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                </div>
              </div>
            )}
            {step === 4 && (
              <>
                <FormField
                  control={form.control}
                  name="requireEmailVerification"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            // Clear password when email verification is checked
                            if (checked) {
                              form.setValue("password", "");
                              setShowPassword(false);
                            }
                          }}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Require email verification</FormLabel>
                        <p className="text-xs text-muted-foreground">
                          User will need to verify their email before accessing
                          the system
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
                {!requireEmailVerificationWatch && (
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => {
                      const passwordValue = field.value || "";
                      const missingRequirements =
                        getMissingPasswordRequirements(passwordValue);
                      return (
                        <FormItem>
                          <FormLabel>Password*</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter password"
                                className="pr-10"
                                {...field}
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                onClick={() =>
                                  setShowPassword((prev) => !prev)
                                }
                                aria-label={
                                  showPassword
                                    ? "Hide password"
                                    : "Show password"
                                }
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          {passwordValue.length > 0 &&
                            missingRequirements.length > 0 && (
                              <p className="text-xs text-red-500" role="alert">
                                {missingRequirements.join(", ")}
                              </p>
                            )}
                          {passwordValue.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                              Password must be at least 7 characters long and
                              contain at least one uppercase letter, one
                              lowercase letter, one number, and one special
                              character.
                            </p>
                          )}
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                )}
              </>
            )}
            <DialogFooter>
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                >
                  Back
                </Button>
              )}
              {step < 4 && (
                <Button
                  type="button"
                  onClick={async () => {
                    // Validate current step before proceeding
                    if (step === 1) {
                      // Validate first name, last name, and email
                      const firstName = form.getValues("firstName");
                      const lastName = form.getValues("lastName");
                      const email = form.getValues("email");

                      if (!firstName || firstName.trim().length < 2) {
                        toast.error(
                          "First name is required and must be at least 2 characters"
                        );
                        return;
                      }

                      if (!lastName || lastName.trim().length < 2) {
                        toast.error(
                          "Last name is required and must be at least 2 characters"
                        );
                        return;
                      }

                      if (!email || !email.trim()) {
                        toast.error("Email is required");
                        return;
                      }

                      // Validate email format
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(email)) {
                        toast.error("Please enter a valid email address");
                        return;
                      }
                    }

                    if (step === 2) {
                      // Validate role selection
                      const role = form.getValues("role");
                      if (!role || role.trim() === "") {
                        toast.error("Please select a role");
                        return;
                      }

                      // Get the selected role object
                      const selectedRole = roles.find(
                        (r) => r.roleCode === role
                      );

                      // Validate role-specific dropdowns
                      if (selectedRole?.roleCode === "PROGRAM_ADMIN") {
                        const clientId = form.getValues("clientId");
                        if (!clientId || clientId.trim() === "") {
                          toast.error(
                            "Please select a client for Program Admin role"
                          );
                          return;
                        }
                      }

                      if (selectedRole?.roleCode === "MERCHANT_ADMIN") {
                        const merchantId = form.getValues("merchantId");
                        if (!merchantId || merchantId.trim() === "") {
                          toast.error(
                            "Please select a merchant for Merchant Admin role"
                          );
                          return;
                        }
                      }

                      if (selectedRole?.roleCode === "MERCHANT_APP") {
                        const merchantId = form.getValues("merchantId");
                        const merchantStoreId =
                          form.getValues("merchantStoreId");
                        if (!merchantId || merchantId.trim() === "") {
                          toast.error(
                            "Please select a merchant for Merchant Application role"
                          );
                          return;
                        }
                        if (!merchantStoreId || merchantStoreId.trim() === "") {
                          toast.error(
                            "Please select a store for Merchant Application role"
                          );
                          return;
                        }
                      }

                      if (selectedRole?.roleCode === "TRANSFER_PARTNER_APP") {
                        const clientId = form.getValues("clientId");
                        const transferPartnerId =
                          form.getValues("transferPartnerId");
                        if (!clientId || clientId.trim() === "") {
                          toast.error(
                            "Please select a client for Transfer Partner Application role"
                          );
                          return;
                        }
                        if (
                          !transferPartnerId ||
                          transferPartnerId.trim() === ""
                        ) {
                          toast.error(
                            "Please select a transfer partner for Transfer Partner Application role"
                          );
                          return;
                        }
                      }

                      if (selectedRole?.roleCode === "GIFT_CARD_ADMIN") {
                        const mallId = form.getValues("mallId");
                        if (!mallId || mallId.trim() === "") {
                          toast.error(
                            "Please enter a Mall ID for Gift Card Admin role"
                          );
                          return;
                        }
                      }

                      if (
                        selectedRole?.roleCode === ROLE_CODES.PERK_POINT_PROGRAM
                      ) {
                        const programId = form.getValues("programId");
                        if (!programId || programId.trim() === "") {
                          toast.error(
                            "Please select a Perk Point program for this role"
                          );
                          return;
                        }
                      }
                    }

                    if (step === 3) {
                      const roleValue = form.getValues("role");
                      const selectedRole = roles.find(
                        (r) => r.roleCode === roleValue
                      );
                      const isGiftCardAdmin =
                        selectedRole?.roleCode === "GIFT_CARD_ADMIN";
                      const isOfferPointAdmin =
                        selectedRole?.roleCode === "OFFER_POINT_ADMIN";
                      const isGamificationAdmin =
                        selectedRole?.roleCode === "GAMIFICATION_ADMIN";
                      const isPerkPointRole =
                        selectedRole?.roleCode ===
                          ROLE_CODES.PERK_POINT_PROGRAM ||
                        selectedRole?.roleCode === ROLE_CODES.PERK_POINT_ADMIN;

                      if (selectedApplications.length === 0) {
                        toast.error("Please select at least one application");
                        return;
                      }

                      if (
                        isGiftCardAdmin &&
                        !selectedApplications.includes("gift_card")
                      ) {
                        toast.error(
                          "Please select Gift Card application for Gift Card Admin role"
                        );
                        return;
                      }

                      if (
                        isOfferPointAdmin &&
                        !selectedApplications.includes("offer_point")
                      ) {
                        toast.error(
                          "Please select Offer Point application for Offer Point Admin role"
                        );
                        return;
                      }

                      if (
                        isGamificationAdmin &&
                        !selectedApplications.includes("gamification")
                      ) {
                        toast.error(
                          "Please select Game Point application for Gamification Admin role"
                        );
                        return;
                      }

                      if (
                        isPerkPointRole &&
                        !selectedApplications.includes("perk_point")
                      ) {
                        toast.error(
                          "Please select Perk Point application for this role"
                        );
                        return;
                      }
                    }

                    setStep(step + 1);
                  }}
                >
                  Next
                </Button>
              )}
              {step === 4 && (
                <>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (!requireEmailVerificationWatch && !isCreatePasswordValid)
                    }
                    onClick={() => onSubmit(form.getValues())}
                  >
                    Create User
                  </Button>
                </>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
